import React from "react";

import classnames from "classnames";
import SBCLinkCell from "ComparePlans/Components/SBCLinkCell";
import formatCurrency from "Utils/formatCurrency";

import { enrollmentForOverview, chooseCoverageRows } from "../Utils/chooseCoverage";
import { TitleSmall } from "Cobalt/Header";
import Button from "Common/Button";
import InfoPopover from "Common/InfoPopover";
import { DISPLAY_CATEGORIES, DISPLAY_TERM_NAMES } from "Common/plans/DentalPlanDetails";
import { ContentRow } from "Common/Table";
import BodyText from "Theme/BodyText";

import makeBem from "Utils/makeBem";

const bem = makeBem("MultipleMedicalPlans");

const pricingTable = (prices, contributions, familyInfo, plans) =>
  chooseCoverageRows(familyInfo, prices, contributions, enrollmentForOverview(prices, familyInfo), "dental").map((row) => (
    <ContentRow
      heading={<BodyText>{row.label}</BodyText>}
      key={row.rowId}
      className={classnames(bem("specificCostRow"))}
      cell={(_, i) => <>{formatCurrency(row.prices[i])}/mo</>}
      items={plans}
    />
  ));
const YourCostCell = () => (
  <span>
    <strong>Your Cost</strong>
    <InfoPopover infoTitle="Payroll Contributions" infoText="This is the amount you pay each month, taken directly out of your paycheck." placement="top" />
  </span>
);

const MultipleDentalPlans = ({ prices, contributions, familyInfo, plans, onClick }) => (
  <>
    <tbody>
      <ContentRow
        cell={YourCostCell}
        heading={
          <TitleSmall mt={0} mb={0}>
            Premium Overview
          </TitleSmall>
        }
        className={classnames(bem("payrollContributonHeaderRow"), "bg-gray-100")}
        items={plans}
      />
      {pricingTable(prices, contributions, familyInfo, plans)}

      <ContentRow
        cell={(p) => (
          <Button
            text="Enroll or Decline"
            color="blue"
            size="medium"
            fullWidth
            className={classnames(bem("selectPlanButton"))}
            data-keyboard="true"
            onClick={(_) => onClick(p.id)}
          />
        )}
        items={plans}
      />
      <ContentRow
        heading={
          <TitleSmall mt={0} mb={0}>
            Plan Overview
          </TitleSmall>
        }
        className="row-separator bg-gray-100"
        cell={(p) => ""}
        items={plans}
      />
      <ContentRow
        heading={<BodyText weight="bold">Maximum Benefit</BodyText>}
        className="row-separator"
        cell={(p) => `${formatCurrency(p.annual_maximum)}`}
        items={plans}
      />
      <ContentRow
        heading={<BodyText weight="bold">Deductible</BodyText>}
        className="row-separator"
        cell={(p) => `${formatCurrency(p.deductible)}`}
        items={plans}
      />
      <ContentRow heading={<BodyText weight="bold">Preventive Care</BodyText>} className="row-separator" cell={(p) => p.preventive_care} items={plans} />
      <ContentRow heading={<BodyText weight="bold">Cavities</BodyText>} className="row-separator" cell={(p) => p.cavities} items={plans} />

      <ContentRow
        heading={
          <TitleSmall mt={0} mb={0}>
            Plan Details
          </TitleSmall>
        }
        className="row-separator bg-gray-100"
        cell={(p) => ""}
        items={plans}
      />

      <ContentRow heading={<BodyText weight="bold">Preventive</BodyText>} className="row-separator" cell={(p) => ""} items={plans} />
      {DISPLAY_CATEGORIES.Preventive.map((term) => (
        <ContentRow
          heading={<BodyText ml={2}>{DISPLAY_TERM_NAMES[term] || term}</BodyText>}
          className="row-separator"
          cell={(p) => p.fine_print.Preventive[term]}
          items={plans}
        />
      ))}

      <ContentRow heading={<BodyText weight="bold">Crowns</BodyText>} className="row-separator" cell={(p) => ""} items={plans} />
      {DISPLAY_CATEGORIES.Crowns.map((term) => (
        <ContentRow
          heading={<BodyText ml={2}>{DISPLAY_TERM_NAMES[term] || term}</BodyText>}
          className="row-separator"
          cell={(p) => p.fine_print.Crowns[term]}
          items={plans}
        />
      ))}

      <ContentRow heading={<BodyText weight="bold">Periodontics</BodyText>} className="row-separator" cell={(p) => ""} items={plans} />
      {DISPLAY_CATEGORIES.Periodontics.map((term) => (
        <ContentRow
          heading={
            <BodyText ml={2}>
              {(DISPLAY_TERM_NAMES[term] || term).split("<br>").map((text, index) => (
                <React.Fragment key={index}>
                  {index !== 0 && <br />}
                  {text}
                </React.Fragment>
              ))}
            </BodyText>
          }
          className="row-separator"
          cell={(p) => p.fine_print.Periodontics[term]}
          items={plans}
        />
      ))}

      <ContentRow heading={<BodyText weight="bold">Endodontics</BodyText>} className="row-separator" cell={(p) => ""} items={plans} />
      {DISPLAY_CATEGORIES.Endodontics.map((term) => (
        <ContentRow
          heading={<BodyText ml={2}>{DISPLAY_TERM_NAMES[term] || term}</BodyText>}
          className="row-separator"
          cell={(p) => p.fine_print.Endodontics[term]}
          items={plans}
        />
      ))}

      <ContentRow heading={<BodyText weight="bold">Tooth Extraction</BodyText>} className="row-separator" cell={(p) => ""} items={plans} />
      {DISPLAY_CATEGORIES["Tooth Extraction"].map((term) => (
        <ContentRow
          heading={<BodyText>{DISPLAY_TERM_NAMES[term] || term}</BodyText>}
          className="row-separator"
          cell={(p) => p.fine_print["Tooth Extraction"][term]}
          items={plans}
        />
      ))}

      <ContentRow heading={<BodyText weight="bold">Orthodontics</BodyText>} className="row-separator" cell={(p) => ""} items={plans} />
      {DISPLAY_CATEGORIES.Orthodontics.map((term) => (
        <ContentRow
          heading={<BodyText>{DISPLAY_TERM_NAMES[term] || term}</BodyText>}
          className="row-separator"
          cell={(p) => p.fine_print.Orthodontics[term]}
          items={plans}
        />
      ))}

      <ContentRow
        heading={<BodyText weight="bold">Summary of Benefits & Coverage</BodyText>}
        className="row-separator sbc"
        cell={(plan) => <SBCLinkCell plan={plan} />}
        items={plans}
      />
    </tbody>
    <ContentRow
      cell={(p) => (
        <Button
          text="Enroll or Decline"
          size="medium"
          fullWidth
          className={classnames(bem("selectPlanButton"))}
          data-keyboard="true"
          onClick={(_) => onClick(p.id)}
        />
      )}
      items={plans}
    />
  </>
);

export default MultipleDentalPlans;
